import {
  isAllowedChatLinkHref,
  normalizeChatLinkHref,
} from 'components/@home/@messages/ChatPanel/SendForm/controls/ChatField/ChatInput/contentEditableSerializers';
import { useCallback, useRef, useState } from 'react';
import { findClosestAllowedLink, unwrapChatLink } from 'utils/chatComposerLink';

/**
 * Link insert/edit popover state for a contenteditable composer (message field or image caption).
 */
export default function useChatComposerLinkPopover(inputRef) {
  const linkApplyModeRef = useRef(null);
  const linkSavedRangeRef = useRef(null);
  const linkEditTargetRef = useRef(null);
  const [linkPopover, setLinkPopover] = useState({
    open: false,
    anchorEl: null,
    showRemove: false,
    initialUrl: '',
  });

  const closeLinkPopover = useCallback(() => {
    linkApplyModeRef.current = null;
    linkSavedRangeRef.current = null;
    linkEditTargetRef.current = null;
    setLinkPopover({
      open: false,
      anchorEl: null,
      showRemove: false,
      initialUrl: '',
      initialText: '',
    });
  }, []);

  const openLinkPopoverFromEditor = useCallback(
    triggerEl => {
      const el = inputRef.current;
      if (!el || el.contentEditable !== 'true' || !triggerEl) return;
      el.focus();
      const sel = window.getSelection();

      const getVirtualAnchor = () => {
        if (!sel.rangeCount || !el.contains(sel.anchorNode)) return triggerEl;
        const range = sel.getRangeAt(0);
        let rect = range.getBoundingClientRect();
        if (rect.width === 0 && rect.height === 0) {
          const span = document.createElement('span');
          span.appendChild(document.createTextNode('\u200b'));
          range.insertNode(span);
          rect = span.getBoundingClientRect();
          span.parentNode.removeChild(span);
        }
        return {
          getBoundingClientRect: () => rect,
          nodeType: 1,
        };
      };

      if (!sel.rangeCount || !el.contains(sel.anchorNode)) {
        const r = document.createRange();
        r.selectNodeContents(el);
        r.collapse(false);
        linkSavedRangeRef.current = r.cloneRange();
        linkEditTargetRef.current = null;
        linkApplyModeRef.current = 'caret';
        setLinkPopover({
          open: true,
          anchorEl: getVirtualAnchor(),
          showRemove: false,
          initialUrl: '',
          initialText: '',
        });
        return;
      }
      const range = sel.getRangeAt(0);
      const linkEl = findClosestAllowedLink(sel.anchorNode, el);
      if (linkEl) {
        linkEditTargetRef.current = linkEl;
        linkSavedRangeRef.current = null;
        linkApplyModeRef.current = 'edit';
        const href = linkEl.getAttribute('href') || '';
        setLinkPopover({
          open: true,
          anchorEl: linkEl, // Always anchor to the link element itself when editing
          showRemove: true,
          initialUrl: href,
          initialText: linkEl.textContent || '',
        });
        return;
      }

      const anchorEl = getVirtualAnchor();
      linkEditTargetRef.current = null;

      if (!range.collapsed) {
        linkSavedRangeRef.current = range.cloneRange();
        linkApplyModeRef.current = 'selection';
        setLinkPopover({
          open: true,
          anchorEl,
          showRemove: false,
          initialUrl: '',
          initialText: sel.toString() || '',
        });
      } else {
        linkSavedRangeRef.current = range.cloneRange();
        linkApplyModeRef.current = 'caret';
        setLinkPopover({
          open: true,
          anchorEl,
          showRemove: false,
          initialUrl: '',
          initialText: '',
        });
      }
    },
    [inputRef],
  );

  const handleLinkToolbarClick = useCallback(
    e => {
      openLinkPopoverFromEditor(e.currentTarget);
    },
    [openLinkPopoverFromEditor],
  );

  const handleLinkEditFromInput = useCallback(linkEl => {
    if (!linkEl) return;
    linkEditTargetRef.current = linkEl;
    linkSavedRangeRef.current = null;
    linkApplyModeRef.current = 'edit';
    const href = linkEl.getAttribute('href') || '';
    setLinkPopover({
      open: true,
      anchorEl: linkEl,
      showTextField: true,
      showRemove: true,
      initialUrl: href,
      initialText: linkEl.textContent || '',
    });
  }, []);

  const handleLinkApply = useCallback(
    (url, text) => {
      const el = inputRef.current;
      const href = normalizeChatLinkHref(url);
      const linkText = String(text || '').trim() || href;
      if (!el || !isAllowedChatLinkHref(href)) return;
      const mode = linkApplyModeRef.current;
      el.focus();
      const sel = window.getSelection();

      if (mode === 'edit' && linkEditTargetRef.current) {
        const a = linkEditTargetRef.current;
        a.setAttribute('href', href);
        a.setAttribute('rel', 'noopener noreferrer');
        if (text !== undefined && a.textContent !== linkText) {
          a.textContent = linkText;
        }
      } else if (mode === 'selection' && linkSavedRangeRef.current) {
        sel.removeAllRanges();
        sel.addRange(linkSavedRangeRef.current);
        const originalText = linkSavedRangeRef.current.toString();
        // If the user changed the text in the popover, we replace the selection entirely
        if (text !== undefined && text.trim() && text.trim() !== originalText) {
          const range = sel.getRangeAt(0);
          range.deleteContents();
          const a = document.createElement('a');
          a.setAttribute('href', href);
          a.setAttribute('rel', 'noopener noreferrer');
          a.textContent = linkText;
          range.insertNode(a);
        } else {
          document.execCommand('createLink', false, href);
          const created = findClosestAllowedLink(sel.anchorNode, el);
          if (created) created.setAttribute('rel', 'noopener noreferrer');
        }
      } else if (mode === 'caret' && linkSavedRangeRef.current) {
        sel.removeAllRanges();
        sel.addRange(linkSavedRangeRef.current);
        const range = sel.getRangeAt(0);
        range.deleteContents();
        const a = document.createElement('a');
        a.setAttribute('href', href);
        a.setAttribute('rel', 'noopener noreferrer');
        a.textContent = linkText;
        range.insertNode(a);
        range.setStartAfter(a);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
      }

      el.dispatchEvent(new Event('input', { bubbles: true }));
    },
    [inputRef],
  );

  const handleLinkRemove = useCallback(() => {
    const el = inputRef.current;
    const a = linkEditTargetRef.current;
    if (!el || !a) {
      closeLinkPopover();
      return;
    }
    el.focus();
    unwrapChatLink(a);
    el.dispatchEvent(new Event('input', { bubbles: true }));
    closeLinkPopover();
  }, [inputRef, closeLinkPopover]);

  const handleLinkShortcut = useCallback(() => {
    openLinkPopoverFromEditor(inputRef.current);
  }, [inputRef, openLinkPopoverFromEditor]);

  return {
    linkPopover,
    closeLinkPopover,
    handleLinkToolbarClick,
    handleLinkEditFromInput,
    handleLinkApply,
    handleLinkRemove,
    handleLinkShortcut,
  };
}
